import { Style, Fill, Stroke, Text } from 'ol/style';
import { hexToRGB, memoizeStyleFunction, cloneStyleValue, convertSize } from './styleUtils';
import evaluate, { expressionOrDefault } from '../olEvaluator';
import { emptyStyle } from './static';
import { Geometry } from 'ol/geom';

/**
 * @private
 * Get the static OL style instance for a text symbolizer.
 * The text and placement properties will be set on the style object at runtime.
 * @param {object} textsymbolizer SLD text symbolizer object.
 * @return {object} openlayers style
 */

function textStyle(textsymbolizer) {
  if (!textsymbolizer) {
    return emptyStyle;
  }

  if (!textsymbolizer.forEach) {
    return textStyleSingle(textsymbolizer);
  }

  const result = [];
  textsymbolizer.forEach((symb) => {
    result.push(textStyleSingle(symb));
  });
  return result;
}

function textStyleSingle(textsymbolizer) {
  if (!(textsymbolizer && textsymbolizer.label)) {
    return emptyStyle;
  }

  // If the label is dynamic, set text to empty string.
  // In that case, text will be set at runtime.
  const labelText = expressionOrDefault(textsymbolizer.label, '');

  const fill = textsymbolizer.fill ? textsymbolizer.fill.styling : {};
  const halo =
    textsymbolizer.halo && textsymbolizer.halo.fill
      ? textsymbolizer.halo.fill.styling
      : {};
  const haloRadius = resolveStaticNumericValue(
    textsymbolizer && textsymbolizer.halo && textsymbolizer.halo.radius,
    1
  );
  const fontStyling =
    textsymbolizer.font && textsymbolizer.font.styling
      ? textsymbolizer.font.styling
      : {};
  const {
    fontFamily = 'sans-serif',
    fontStyle = '',
    fontWeight = '',
  } = fontStyling;
  const fontSize = resolveStaticValue(fontStyling.fontSize, 10);

  const staticFillValue = resolveStaticValue(fill.fill, '#000000');
  const staticFillOpacity = resolveStaticPropValue(fill.fillOpacity, 1);

  const pointplacement =
    textsymbolizer &&
    textsymbolizer.labelplacement &&
    textsymbolizer.labelplacement.pointplacement
      ? textsymbolizer.labelplacement.pointplacement
      : {};

  // If rotation is dynamic, default to 0. Rotation will be set at runtime.
  const rotationRaw = pointplacement.rotation;
  const labelRotationDegrees =
    rotationRaw && rotationRaw.prop
      ? 0.0
      : expressionOrDefault(rotationRaw, 0.0);

  const displacement =
    pointplacement && pointplacement.displacement
      ? pointplacement.displacement
      : {};
  const offsetX = resolveStaticValue(displacement.displacementx, 0);
  const offsetY = resolveStaticValue(displacement.displacementy, 0);

  // OpenLayers does not support fractional alignment, so snap the anchor to the most suitable option.
  const anchorpoint = (pointplacement && pointplacement.anchorpoint) || {};

  let textAlign = 'center';
  const anchorpointx = Number(
    anchorpoint.anchorpointx === '' ? NaN : anchorpoint.anchorpointx
  );
  if (anchorpointx < 0.25) {
    textAlign = 'left';
  } else if (anchorpointx > 0.75) {
    textAlign = 'right';
  }

  let textBaseline = 'middle';
  const anchorpointy = Number(
    anchorpoint.anchorpointy === '' ? NaN : anchorpoint.anchorpointy
  );
  if (anchorpointy < 0.25) {
    textBaseline = 'bottom';
  } else if (anchorpointy > 0.75) {
    textBaseline = 'top';
  }

  // Assemble text style options.
  const textStyleOptions = {
    text: labelText,
    font: `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`,
    offsetX: Number(offsetX),
    offsetY: Number(offsetY),
    rotation: (Math.PI * labelRotationDegrees) / 180.0,
    textAlign,
    textBaseline,
    fill: new Fill({
      color: buildColor(staticFillValue, staticFillOpacity),
    }),
  };

  // Convert SLD halo to text symbol stroke.
  if (textsymbolizer.halo) {
    textStyleOptions.stroke = new Stroke({
      color: buildColor(halo.fill, halo.fillOpacity),
      width: toHaloStrokeWidth(haloRadius),
    });
  }

  const style = new Style({
    text: new Text(textStyleOptions),
  });

  style.xfontsize = fontSize;
  return style;
}

const cachedTextStyle = memoizeStyleFunction(textStyle);

/**
 * @private
 * Get an OL text style instance for a feature according to a symbolizer.
 * @param {object} symbolizer SLD symbolizer object.
 * @param {ol/Feature} feature OpenLayers Feature.
 * @param {Function} getProperty A property getter: (feature, propertyName) => property value.
 * @returns {ol/Style} OpenLayers style instance.
 */
function getTextStyle(symbolizer, feature, getProperty) {
    if (feature.get('features') && feature.get('features').length == 1) {
        feature = feature.get('features')[0];
    }

  const olStyle = cloneStyleValue(cachedTextStyle(symbolizer));

  if (olStyle.forEach) {
    return olStyle;
  }

  if (feature.get('xstate') == null || feature.get('xstate') == 0) {
    olStyle.setZIndex(0);
  } else {
    olStyle.setZIndex(100);
  }
  const olText = olStyle.getText();
  if (!olText) {
    return olStyle;
  }

  const fill = symbolizer.fill ? symbolizer.fill.styling : {};
  const fontStyling =
    symbolizer.font && symbolizer.font.styling
      ? symbolizer.font.styling
      : {};
  const haloStyling =
    symbolizer.halo && symbolizer.halo.fill
      ? symbolizer.halo.fill.styling
      : {};

  if (fill && (fill.fill || fill.fillOpacity)) {
    const fillValue = resolveValue(fill.fill, feature);
    const fillOpacityValue = resolveValue(fill.fillOpacity, feature);
    const fillColor = buildColor(fillValue, fillOpacityValue);

    if (fillColor != null) {
      olText.setFill(new Fill({ color: fillColor }));
    }
  }

  // Read text from feature and set it on the text style instance.
  const { label, labelplacement } = symbolizer;

  let textWidth = 0;

  // Set text only if the label expression is dynamic.
  if (label && label.type === 'expression') {
    const labelText = evaluate(label, feature, getProperty);
    const labelString = labelText != null ? labelText.toString() : '';
    textWidth = estimateTextWidth(labelString, olStyle.xfontsize);
    olText.setText(labelString);
  }

  // Set rotation if expression is dynamic.
  if (labelplacement) {
    const pointPlacementRotation =
      (labelplacement.pointplacement &&
        labelplacement.pointplacement.rotation) ||
      0.0;
    if (pointPlacementRotation.type === 'expression') {
      const labelRotationDegrees = evaluate(
        pointPlacementRotation,
        feature,
        getProperty
      );
      olText.setRotation((Math.PI * labelRotationDegrees) / 180.0); // OL rotation is in radians.
    }

    if (
      labelplacement.pointplacement &&
      labelplacement.pointplacement.rotation &&
      labelplacement.pointplacement.rotation.prop
    ) {
      const labelRotationDegrees = feature.get(
        labelplacement.pointplacement.rotation.prop
      );
      olText.setRotation((Math.PI * labelRotationDegrees) / 180.0);
    }
  }

  if (labelplacement && labelplacement.pointplacement) {
    const displacement =
      labelplacement.pointplacement.displacement || {};
    const offsetXValue = resolveValue(displacement.displacementx, feature);
    const offsetYValue = resolveValue(displacement.displacementy, feature);
    const numericOffsetX = convertSize(
      Number(offsetXValue),
      feature.get('currentResolution'),
      feature.get('currentUom'),
      symbolizer.uom
    );
    const numericOffsetY = convertSize(
      Number(offsetYValue),
      feature.get('currentResolution'),
      feature.get('currentUom'),
      symbolizer.uom
    );
    if (!Number.isNaN(numericOffsetX)) {
      olText.setOffsetX(numericOffsetX);
    }
    if (!Number.isNaN(numericOffsetY)) {
      olText.setOffsetY(numericOffsetY);
    }
  }

  if (fontStyling) {
    const fontSizeValue = resolveValue(fontStyling.fontSize, feature);
    if (fontSizeValue != null) {
      const fontFamily = fontStyling.fontFamily || 'sans-serif';
      const fontStyle = fontStyling.fontStyle || '';
      const fontWeight = fontStyling.fontWeight || '';
      const numericFontSize = convertSize(Number(fontSizeValue), feature.get('currentResolution'), feature.get('currentUom'), symbolizer.uom);

      if (!Number.isNaN(numericFontSize)) {
        olText.setFont(
          `${fontStyle} ${fontWeight} ${numericFontSize}px ${fontFamily}`
        );
        olStyle.xfontsize = numericFontSize;
      }
    }
  }

  if (symbolizer.halo) {
    const haloRadiusValue = resolveValue(symbolizer.halo.radius, feature);
    const numericHaloRadius = convertSize(
      Number(haloRadiusValue),
      feature.get('currentResolution'),
      feature.get('currentUom'),
      symbolizer.uom
    );
    const haloFillValue = resolveValue(haloStyling.fill, feature);
    const haloFillOpacityValue = resolveValue(haloStyling.fillOpacity, feature);
    const haloColor = buildColor(haloFillValue, haloFillOpacityValue);

    let haloStroke = olText.getStroke();
    if (!haloStroke) {
      haloStroke = new Stroke({
        color: haloColor != null ? haloColor : '#000000',
        width: toHaloStrokeWidth(1),
      });
      olText.setStroke(haloStroke);
    }

    if (haloColor != null) {
      haloStroke.setColor(haloColor);
    } else if (haloFillOpacityValue || haloFillOpacityValue === 0) {
      const currentColor = haloStroke.getColor();
      if (isHexColor(currentColor)) {
        haloStroke.setColor(hexToRGB(currentColor, haloFillOpacityValue));
      }
    }

    if (!Number.isNaN(numericHaloRadius)) {
      haloStroke.setWidth(toHaloStrokeWidth(numericHaloRadius));
    }
  }

  // Set line or point placement according to geometry type.
  let geometry = feature.getGeometry ? feature.getGeometry() : feature.geometry;

  if (geometry == null && feature instanceof Geometry) {
    geometry = feature;
  }

  const geometryType = geometry.getType ? geometry.getType() : geometry.type;
  const lineplacement =
    symbolizer &&
    symbolizer.labelplacement &&
    symbolizer.labelplacement.lineplacement
      ? symbolizer.labelplacement.lineplacement
      : null;
  const placement =
    geometryType !== 'point' && lineplacement ? 'line' : 'point';
  olText.setPlacement(placement);

  if (placement === 'line') {
    if (lineplacement.perpendicularoffset) {
      olText.setOffsetY(lineplacement.perpendicularoffset);
    }

    if (
      symbolizer.vendoroption != null &&
      symbolizer.vendoroption.repeat != null &&
      symbolizer.vendoroption.repeat > 0
    ) {
      olText.setRepeat(symbolizer.vendoroption.repeat + textWidth);
      }
  }

  return olStyle;
}

function estimateTextWidth(text, fontSize = 16) {
    let total = 0;
    for (const char of text) {
        if ("MW@#".includes(char)) total += fontSize * 0.9; // wide
        else if ("il.,'".includes(char)) total += fontSize * 0.3; // narrow
        else total += fontSize * 0.6; // average
    }
    return total + 10;
}

function resolveValue(value, feature) {
  if (value && value.prop) {
    return feature.get(value.prop);
  }
  if (value && value.type === 'propertyname') {
    return feature.get(value.value);
  }
  return value;
}

function resolveStaticValue(value, fallback) {
  if (value && value.prop) {
    return fallback;
  }
  return value != null ? value : fallback;
}

function resolveStaticPropValue(value, fallback) {
  if (value && value.prop) {
    return fallback;
  }
  return value;
}

function resolveStaticNumericValue(value, fallback) {
  if (value && (value.prop || value.type === 'propertyname' || value.type === 'expression')) {
    return fallback;
  }
  if (value == null || value === '') {
    return fallback;
  }
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? fallback : numericValue;
}

function buildColor(value, opacity) {
  if ((opacity || opacity === 0) && isHexColor(value)) {
    return hexToRGB(value, opacity);
  }
  return value;
}

function isHexColor(value) {
  return value && value.slice && value.slice(0, 1) === '#';
}

function toHaloStrokeWidth(radius) {
  const numericRadius = Number(radius);
  if (Number.isNaN(numericRadius)) {
    return 2;
  }

  // Keep existing workaround for OL halo rendering when width is exactly 4 or 8.
  const adjustedRadius =
    numericRadius === 2 || numericRadius === 4
      ? numericRadius - 0.00001
      : numericRadius;

  return adjustedRadius * 2;
}

export default getTextStyle;
